import React, { FC, useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { adminCloseStationAction } from "../../../redux/actions/cashierStationActions";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

interface CloseStationDrawerProps {
  open: boolean;
  onClose: () => void;
  stationAccessSessionId: number | null;
  stationName: string;
  onSuccess?: () => void;
}

export const CloseStationDrawer: FC<CloseStationDrawerProps> = ({
  open,
  onClose,
  stationAccessSessionId,
  stationName,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { adminCloseStationLoading, adminCloseStationError } = useAppSelector(
    (state) => state.cashierStation
  );

  const [billTotal, setBillTotal] = useState<string>("");
  const [oneCent, setOneCent] = useState<string>("0");
  const [fiveCents, setFiveCents] = useState<string>("0");
  const [tenCents, setTenCents] = useState<string>("0");
  const [twentyFiveCents, setTwentyFiveCents] = useState<string>("0");

  useEffect(() => {
    if (!open) {
      // Reset form when drawer closes
      setBillTotal("");
      setOneCent("0");
      setFiveCents("0");
      setTenCents("0");
      setTwentyFiveCents("0");
    }
  }, [open]);

  const handleClose = () => {
    if (!adminCloseStationLoading) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!stationAccessSessionId) {
      snackbar.showSnackbar("Invalid station session", "error");
      return;
    }

    if (!billTotal || isNaN(Number(billTotal)) || Number(billTotal) < 0) {
      snackbar.showSnackbar("Please enter a valid bill total", "error");
      return;
    }

    try {
      await dispatch(
        adminCloseStationAction({
          stationAccessId: stationAccessSessionId,
          stationBalanceDto: {
            billTotal: Number(billTotal),
            oneCent: Number(oneCent),
            fiveCents: Number(fiveCents),
            tenCents: Number(tenCents),
            twentyFiveCents: Number(twentyFiveCents),
          },
        })
      ).unwrap();

      snackbar.showSnackbar(
        `Station ${stationName} closed successfully`,
        "success"
      );
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      snackbar.showSnackbar(
        error?.message || "Failed to close station",
        "error"
      );
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: "35rem",
          padding: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: Colors.TEXT_GRAY_DARK }}
        >
          Close Station - {stationName}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          Please enter the closing balance for this station. This action will
          mark the station as closed.
        </Typography>

        {adminCloseStationError && (
          <Alert severity="error">{adminCloseStationError}</Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Bill Total
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Bill Total ($)"
            value={billTotal}
            onChange={(e) => setBillTotal(e.target.value)}
            required
            inputProps={{ min: 0, step: "0.01" }}
            disabled={adminCloseStationLoading}
          />

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", marginTop: "1rem" }}
          >
            Cents
          </Typography>

          <TextField
            fullWidth
            type="number"
            label="$0.01"
            value={oneCent}
            onChange={(e) => setOneCent(e.target.value)}
            inputProps={{ min: 0 }}
            disabled={adminCloseStationLoading}
          />

          <TextField
            fullWidth
            type="number"
            label="$0.05"
            value={fiveCents}
            onChange={(e) => setFiveCents(e.target.value)}
            inputProps={{ min: 0 }}
            disabled={adminCloseStationLoading}
          />

          <TextField
            fullWidth
            type="number"
            label="$0.10"
            value={tenCents}
            onChange={(e) => setTenCents(e.target.value)}
            inputProps={{ min: 0 }}
            disabled={adminCloseStationLoading}
          />

          <TextField
            fullWidth
            type="number"
            label="$0.25"
            value={twentyFiveCents}
            onChange={(e) => setTwentyFiveCents(e.target.value)}
            inputProps={{ min: 0 }}
            disabled={adminCloseStationLoading}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            marginTop: "2rem",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={adminCloseStationLoading}
            sx={{ minWidth: "8rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={adminCloseStationLoading}
            sx={{ minWidth: "8rem", backgroundColor: Colors.PRIMARY }}
          >
            {adminCloseStationLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Close Station"
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

